import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Pill, ShieldCheck, ScanBarcode, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) toast.error(res.error || "Authentication failed");
    else toast.success("Welcome back");
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-slate-50">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-[52%] pine-gradient text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 clinical-grid opacity-30" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center border border-white/15">
              <Pill className="w-7 h-7" />
            </div>
            <div>
              <div className="font-display font-extrabold text-2xl">Trustmeds Pharma</div>
              <div className="text-emerald-200 text-xs tracking-widest uppercase">trustmedspharma.com</div>
            </div>
          </div>
        </div>

        <div className="relative max-w-lg">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tight">
            Clinical precision, <span className="text-emerald-300">counter speed.</span>
          </h1>
          <p className="mt-5 text-emerald-100/90 text-lg leading-relaxed">
            The pharmacy operating system for high-velocity Indian counters — strip &amp;
            tablet billing, dynamic UPI QR, GST invoices, and live inventory in one flow.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-3">
            {[
              { icon: ScanBarcode, title: "Live medicine search + auto stock deduction" },
              { icon: ShieldCheck, title: "GST-ready invoices with dynamic UPI QR" },
              { icon: Pill, title: "Excel/CSV bulk import for entire drug catalogue" },
            ].map((f) => (
              <div key={f.title} className="flex items-center gap-3 text-emerald-50/95">
                <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center">
                  <f.icon className="w-4 h-4" />
                </div>
                <span className="text-sm">{f.title}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-xs text-emerald-200/70">
          © {new Date().getFullYear()} Trustmeds Pharma. All rights reserved.
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md p-8 shadow-lg border-slate-200 animate-slide-up" data-testid="login-form">
          <div className="lg:hidden mb-6 flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-900 flex items-center justify-center">
              <Pill className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="font-display font-extrabold text-lg text-emerald-900">Trustmeds Pharma</div>
              <div className="text-slate-500 text-xs">trustmedspharma.com</div>
            </div>
          </div>

          <h2 className="font-display text-2xl font-extrabold text-slate-900 tracking-tight">
            Sign in to your counter
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Enter credentials or continue with Google. Staff accounts are created by Admin.
          </p>

          <form onSubmit={submit} className="space-y-4 mt-6">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" placeholder="you@pharmacy.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full bg-emerald-900 hover:bg-emerald-800 text-white h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <div className="h-px bg-slate-200 flex-1" />
            OR
            <div className="h-px bg-slate-200 flex-1" />
          </div>

          <Button
            variant="outline"
            onClick={loginWithGoogle}
            data-testid="google-signin-btn"
            className="w-full h-11 border-slate-200 bg-white hover:bg-slate-50"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-xs text-slate-400 text-center mt-5">
            Protected by JWT + secure httpOnly cookies. Session valid 7 days.
          </p>
        </Card>
      </div>
    </div>
  );
}
